import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { CircularProgress, Divider } from '@mui/material';
import { useSelector } from 'react-redux';
import apiClient from '../services/apiClient';
import notify from '../services/notify';
import { getCurrency } from '../services/tableHelper';
import Header from '../components/Header';
import { CreateButton } from '../components/SharedComponents';
import ConfirmationModal from '../components/ConfirmationModal';
import Plans from '../components/subscription/Plans';
import CurrentSubscriptionComponent from '../components/subscription/CurrentSubscription';
import { CurrentSubscription } from '../types';
import { isWithinPaidPeriod } from '../utils/helpers';
import { useSubscription } from '../hooks/useSubscription';
import { AxiosError } from 'axios';

interface SubscriptionPlan {
	id: number;
	name: string;
	display_name: string;
	billing_interval: 'monthly' | 'annually';
	price: number;
	currency: string;
	features: {
		analytics: boolean;
		emailSupport: boolean;
		invoiceReadReceipts: boolean;
		onlineStore: boolean;
	};
	sales_limit: number | null;
	products_limit: number | null;
	team_members_limit: number | null;
	is_active: boolean;
	created_at: string;
	updated_at: string;
}

interface PlansResponse {
	monthly: SubscriptionPlan[];
	annually: SubscriptionPlan[];
}

const Wrapper = styled.div`
	background-color: white;
	width: 100%;
	min-height: 100vh;
	padding: 30px 40px;
	box-sizing: border-box;
	@media (max-width: 768px) {
		padding: 20px 20px;
	}
`;

const Subscription: React.FC = () => {
	const [loading, setLoading] = useState(true);
	const [plansData, setPlansData] = useState<PlansResponse>({ monthly: [], annually: [] });
	const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
	const { isTrialing } = useSubscription();
	const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
	const [upgradeConfirmOpen, setUpgradeConfirmOpen] = useState(false);
	const [upgradeDetails, setUpgradeDetails] = useState<any>(null);
	const [downgradeConfirmOpen, setDowngradeConfirmOpen] = useState(false);
	const [downgradeDetails, setDowngradeDetails] = useState<any>(null);
	const [cancelDowngradeConfirmOpen, setCancelDowngradeConfirmOpen] = useState(false);
	const [reactivateConfirmOpen, setReactivateConfirmOpen] = useState(false);
	const [processing, setProcessing] = useState(false);
	const [selectedPeriod, setSelectedPeriod] = useState<'monthly' | 'annually'>('monthly');
	const [showPlans, setShowPlans] = useState(false);

	const shopState = useSelector((state: any) => state.auth.shop);
	const currency = shopState?.currency || 'GHS';

	// Helper function to check if cancelled subscription is still active
	const isCancelledButActive = (subscription: CurrentSubscription | null) => {
		if (subscription?.status === 'canceled') {
			// If no current_period_end, treat as expired (show banner)
			if (!subscription.current_period_end) {
				return false;
			}
			const now = new Date();
			const periodEnd = new Date(subscription.current_period_end);
			return periodEnd > now; // Still within paid period (true = still active, false = expired)
		}
		return false;
	};

	useEffect(() => {
		fetchSubscriptionData();
	}, []);

	// Show plans if user doesn't have a subscription, is in trial, or if they want to change plans
	useEffect(() => {
		if (
			!currentSubscription ||
			currentSubscription.status === 'canceled' ||
			currentSubscription.status === 'past_due' ||
			isTrialing
		) {
			setShowPlans(true);
		} else {
			setShowPlans(false);
		}
	}, [currentSubscription, isTrialing]);

	const fetchSubscriptionData = async () => {
		try {
			setLoading(true);
			const [plansResponse, currentResponse] = await Promise.all([
				apiClient.get('/subscriptions/plans'),
				apiClient.get('/subscriptions/current'),
			]);

			// Try both possible data paths for compatibility
			const plansData = plansResponse.data;
			setPlansData(plansData || { monthly: [], annually: [] });

			// Handle the subscription response correctly
			const subscriptionData = currentResponse.data;

			setCurrentSubscription(subscriptionData.subscription);
		} catch (error) {
			console.error('Failed to load the subscription data:', error);
			notify('error', 'Failed to load subscription data');
			// setPlansData({ monthly: [], annually: [] }); // Set empty arrays as fallback
		} finally {
			setLoading(false);
		}
	};

	const calculateProratedAmount = (
		currentPlan: SubscriptionPlan,
		newPlan: SubscriptionPlan,
		daysRemaining: number
	) => {
		const currentPlanDailyRate = currentPlan.price / 100 / 30; // Convert from pesewas to cedis
		const newPlanDailyRate = newPlan.price / 100 / 30;

		const remainingValueCurrentPlan = currentPlanDailyRate * daysRemaining;
		const remainingValueNewPlan = newPlanDailyRate * daysRemaining;

		return Math.max(0, remainingValueNewPlan - remainingValueCurrentPlan);
	};

	const getFeatureDifferences = (currentPlan: SubscriptionPlan, newPlan: SubscriptionPlan) => {
		const lostFeatures = [];

		// Check limits that will be reduced
		if (currentPlan.sales_limit === null && newPlan.sales_limit !== null) {
			lostFeatures.push(`Sales limit reduced from unlimited to ${newPlan.sales_limit} per month`);
		} else if (currentPlan.sales_limit && newPlan.sales_limit && newPlan.sales_limit < currentPlan.sales_limit) {
			lostFeatures.push(
				`Sales limit reduced from ${currentPlan.sales_limit} to ${newPlan.sales_limit} per month`
			);
		}

		if (currentPlan.products_limit === null && newPlan.products_limit !== null) {
			lostFeatures.push(`Product limit reduced from unlimited to ${newPlan.products_limit} products`);
		} else if (
			currentPlan.products_limit &&
			newPlan.products_limit &&
			newPlan.products_limit < currentPlan.products_limit
		) {
			lostFeatures.push(
				`Product limit reduced from ${currentPlan.products_limit} to ${newPlan.products_limit} products`
			);
		}

		if (currentPlan.team_members_limit === null && newPlan.team_members_limit !== null) {
			lostFeatures.push(`Team member limit reduced from unlimited to ${newPlan.team_members_limit} members`);
		} else if (
			currentPlan.team_members_limit &&
			newPlan.team_members_limit &&
			newPlan.team_members_limit < currentPlan.team_members_limit
		) {
			lostFeatures.push(
				`Team member limit reduced from ${currentPlan.team_members_limit} to ${newPlan.team_members_limit} members`
			);
		}

		// Check feature flags that will be lost
		if (currentPlan.features?.invoiceReadReceipts && !newPlan.features?.invoiceReadReceipts) {
			lostFeatures.push('Invoice read receipts');
		}

		if (currentPlan.features?.onlineStore && !newPlan.features?.onlineStore) {
			lostFeatures.push('Online store/storefront');
		}

		return lostFeatures;
	};

	const handleUpgradeConfirm = async () => {
		if (!upgradeDetails) return;

		try {
			setProcessing(true);
			const response = await apiClient.post('/subscriptions/upgrade', {
				plan_name: upgradeDetails.plan.name,
				callback_url: `${window.location.origin}/subscription/callback`,
			});

			const authUrl = response.data?.authorization_url;

			if (authUrl) {
				window.location.href = authUrl;
			} else {
				notify('error', 'Failed to get payment authorization URL');
			}
		} catch (error) {
			if (error instanceof AxiosError) {
				notify('error', error.response?.data?.message || 'Failed to upgrade subscription');
			} else {
				notify('error', 'Failed to upgrade subscription');
			}
		} finally {
			setProcessing(false);
			setUpgradeConfirmOpen(false);
		}
	};

	const handleDowngradeConfirm = async () => {
		if (!downgradeDetails) return;

		try {
			setProcessing(true);
			await apiClient.post('/subscriptions/downgrade', {
				plan_name: downgradeDetails.plan.name,
			});

			notify(
				'success',
				`Downgrade to ${downgradeDetails.plan.display_name} scheduled for end of current billing period`
			);
			fetchSubscriptionData();
		} catch (error) {
			if (error instanceof AxiosError) {
				notify('error', error.response?.data?.message || 'Failed to schedule downgrade');
			} else {
				notify('error', 'Failed to schedule downgrade');
			}
		} finally {
			setProcessing(false);
			setDowngradeConfirmOpen(false);
		}
	};

	const handleSubscribe = async (plan: SubscriptionPlan) => {
		try {
			setProcessing(true);

			// If user has an active subscription (not trial), we need to change plans
			if (currentSubscription && isWithinPaidPeriod(currentSubscription)) {
				// Find the current plan details
				const allPlans = [...plansData.monthly, ...plansData.annually];
				const currentPlan = allPlans.find((p) => p.name === currentSubscription.plan_name);

				if (currentPlan) {
					// Convert prices to numbers to ensure proper comparison
					const currentPrice = Number(currentPlan.price);
					const newPrice = Number(plan.price);

					if (newPrice > currentPrice) {
						// This is an upgrade - show upgrade confirmation
						const now = new Date();
						const periodEnd = new Date(currentSubscription.current_period_end);
						const daysRemaining = Math.ceil((periodEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
						const proratedAmount = calculateProratedAmount(currentPlan, plan, daysRemaining);

						// Show upgrade confirmation dialog
						setUpgradeDetails({
							plan,
							currentPlan,
							daysRemaining,
							proratedAmount,
							fullPrice: plan.price / 100, // Convert to cedis for display
							currentPrice: currentPlan.price / 100,
						});
						setUpgradeConfirmOpen(true);
						setProcessing(false);
						return;
					} else if (newPrice < currentPrice) {
						// This is a downgrade - show downgrade confirmation
						const lostFeatures = getFeatureDifferences(currentPlan, plan);

						setDowngradeDetails({
							plan,
							currentPlan,
							lostFeatures,
							currentPrice: currentPlan.price / 100,
							newPrice: plan.price / 100,
						});
						setDowngradeConfirmOpen(true);
						setProcessing(false);
						return;
					}
					// If prices are equal, treat as regular plan change
				}

				// Direct upgrade without confirmation (fallback)
				const response = await apiClient.post('/subscriptions/upgrade', {
					plan_name: plan.name,
					callback_url: `${window.location.origin}/subscription/callback`,
				});

				const authUrl = response.data?.authorization_url;

				if (authUrl) {
					window.location.href = authUrl;
				} else {
					notify('error', 'Failed to get payment authorization URL');
				}
			} else {
				// New subscription
				const response = await apiClient.post('/subscriptions/subscribe', {
					plan_name: plan.name,
					callback_url: `${window.location.origin}/subscription/callback`,
				});

				const authUrl = response.data?.authorization_url;

				if (authUrl) {
					// Redirect to Paystack payment page
					window.location.href = authUrl;
				} else {
					notify('error', 'Failed to get payment authorization URL');
				}
			}
		} catch (error) {
			if (error instanceof AxiosError) {
				notify('error', error.response?.data?.message || 'Failed to subscribe');
			} else {
				notify('error', 'Failed to subscribe');
			}
		} finally {
			setProcessing(false);
		}
	};

	const handleCancelSubscription = async () => {
		try {
			setProcessing(true);
			await apiClient.post('/subscriptions/cancel');
			notify('success', 'Subscription cancelled successfully');
			setCancelDialogOpen(false);
			fetchSubscriptionData();
		} catch (error) {
			if (error instanceof AxiosError) {
				notify('error', error.response?.data?.message || 'Failed to cancel subscription');
			} else {
				notify('error', 'Failed to cancel subscription');
			}
		} finally {
			setProcessing(false);
		}
	};

	const handleCancelDowngrade = async () => {
		try {
			setProcessing(true);
			await apiClient.post('/subscriptions/cancel-downgrade');
			notify('success', 'Downgrade cancelled successfully. You will stay on your current plan.');
			setCancelDowngradeConfirmOpen(false);
			fetchSubscriptionData();
		} catch (error) {
			if (error instanceof AxiosError) {
				notify('error', error.response?.data?.message || 'Failed to cancel downgrade');
			} else {
				notify('error', 'Failed to cancel downgrade');
			}
		} finally {
			setProcessing(false);
		}
	};

	const handleReactivateSubscription = async () => {
		try {
			setProcessing(true);
			const response = await apiClient.post(
				'/subscriptions/reactivate',
				{
					callback_url: `${window.location.origin}/subscription/callback`,
				},
				false
			);

			// If payment is required, redirect to Paystack
			if (response.data.requiresPayment) {
				const authUrl = response.data.data?.authorization_url;
				if (authUrl) {
					window.location.href = authUrl;
				} else {
					notify('error', 'Failed to get payment authorization URL');
				}
			} else {
				notify('success', 'Subscription reactivated successfully. Auto-renewal has been restored.');
				setReactivateConfirmOpen(false);
				fetchSubscriptionData();
			}
		} catch (error) {
			// Check if the error is due to expired subscription
			if (error instanceof AxiosError && error.response?.data?.code === 'SUBSCRIPTION_EXPIRED') {
				// Show message about redirecting to payment
				notify('info', 'Your subscription has expired. Redirecting to payment...');

				// If subscription is expired, try to subscribe to the same plan
				try {
					const planName = currentSubscription?.plan_name || 'starter';
					const response = await apiClient.post('/subscriptions/subscribe', {
						plan_name: planName,
						callback_url: `${window.location.origin}/subscription/callback`,
					});

					const authUrl = response.data?.authorization_url;
					if (authUrl) {
						window.location.href = authUrl;
					} else {
						notify('error', 'Failed to get payment authorization URL');
					}
				} catch (subscribeError) {
					if (subscribeError instanceof AxiosError) {
						notify('error', subscribeError.response?.data?.message || 'Failed to resubscribe');
					} else {
						notify('error', 'Failed to resubscribe');
					}
				}
			} else {
				if (error instanceof AxiosError) {
					notify('error', error.response?.data?.message || 'Failed to reactivate subscription');
				} else {
					notify('error', 'Failed to reactivate subscription');
				}
			}
		} finally {
			setProcessing(false);
		}
	};

	if (loading) {
		return (
			<div className="flex justify-center items-center min-h-screen">
				<CircularProgress />
			</div>
		);
	}

	return (
		<Wrapper>
			<Header
				title="Subscription"
				description="Keep track of your subscription details, update your billing information, and control your account's payment"
				headerComponents={
					currentSubscription &&
					(currentSubscription.status === 'active' ||
						currentSubscription.status === 'pending_downgrade' ||
						currentSubscription.status === 'expired' ||
						currentSubscription.status === 'canceled' ||
						currentSubscription.status === 'past_due') &&
					!isCancelledButActive(currentSubscription)
						? [
								<CreateButton key="change-plan-button" onClick={() => setShowPlans(!showPlans)}>
									<span
										style={{ visibility: showPlans ? 'visible' : 'hidden', position: 'absolute' }}
									>
										Hide Plans
									</span>
									<span style={{ visibility: showPlans ? 'hidden' : 'visible' }}>
										{currentSubscription.status === 'active' ||
										currentSubscription.status === 'pending_downgrade' ||
										isTrialing
											? 'Change Plan'
											: 'Choose Plan'}
									</span>
								</CreateButton>,
						  ]
						: []
				}
			/>
			<div className="md:px-4 sm:max-w-[1200px] mt-4 lg:px-0">
				{/* Current Subscription Component */}
				<CurrentSubscriptionComponent
					subscription={currentSubscription}
					plansData={plansData}
					selectedPeriod={selectedPeriod}
					onCancelClick={() => setCancelDialogOpen(true)}
					onChangePlanClick={() => setShowPlans(!showPlans)}
					onCancelDowngrade={() => setCancelDowngradeConfirmOpen(true)}
					onReactivate={() => setReactivateConfirmOpen(true)}
					showPlans={showPlans}
					currency={currency}
					userTimezone="Africa/Lagos"
					isTrialing={isTrialing}
				/>

				{/* Plans Section - Always rendered but with opacity transition */}
				<div className={`transition-opacity duration-500 ${showPlans ? 'opacity-100' : 'opacity-0'}`}>
					<Divider style={{ backgroundColor: '#e5e7eb', margin: '50px auto', width: '45%', opacity: 0.3 }} />
				</div>

				{/* Plans Component - Show when appropriate */}
				{(showPlans ||
					!currentSubscription ||
					['past_due', 'trialing'].includes(currentSubscription?.status || '') ||
					(currentSubscription?.status === 'canceled' && !isCancelledButActive(currentSubscription))) && (
					<div
						className={`bg-white rounded-lg p-8 mb-8 ${
							showPlans &&
							currentSubscription &&
							['active', 'pending_downgrade'].includes(currentSubscription.status)
								? ''
								: ''
						}`}
					>
						<Plans
							plansData={plansData}
							currentSubscription={currentSubscription}
							selectedPeriod={selectedPeriod}
							onPeriodChange={setSelectedPeriod}
							onPlanSelect={handleSubscribe}
							processing={processing}
							currency={currency}
						/>
					</div>
				)}

				<ConfirmationModal
					isOpen={cancelDialogOpen}
					title="Cancel Subscription?"
					message="Are you sure you want to cancel your subscription? This action will: Cancel your subscription immediately • Remove access to premium features at the end of your current billing period • Stop all future payments • You can resubscribe anytime, but will need to start a new billing cycle"
					confirmText="Cancel Subscription"
					cancelText="Keep Subscription"
					onConfirm={handleCancelSubscription}
					onCancel={() => setCancelDialogOpen(false)}
					isLoading={processing}
					variant="danger"
				/>

				<ConfirmationModal
					isOpen={upgradeConfirmOpen}
					title={`Upgrade to ${upgradeDetails?.plan?.display_name}?`}
					message={
						upgradeDetails
							? `Great choice! You're upgrading from ${upgradeDetails.currentPlan.display_name} to ${
									upgradeDetails.plan.display_name
							  }. 

Since you're upgrading mid-cycle, you'll be charged a reduced amount for your first month to account for the time remaining on your current plan. 

Starting next month, you'll be charged the full ${getCurrency(currency as any)}${
									upgradeDetails.fullPrice
							  } and you'll get immediate access to all the enhanced features!`
							: ''
					}
					confirmText="Upgrade Plan"
					cancelText="Cancel"
					onConfirm={handleUpgradeConfirm}
					onCancel={() => setUpgradeConfirmOpen(false)}
					isLoading={processing}
					variant="info"
				/>

				<ConfirmationModal
					isOpen={downgradeConfirmOpen}
					title={`Downgrade to ${downgradeDetails?.plan?.display_name}?`}
					message={
						downgradeDetails
							? `You're downgrading from ${downgradeDetails.currentPlan.display_name} (${getCurrency(
									currency as any
							  )}${downgradeDetails.currentPrice}) to ${
									downgradeDetails.plan.display_name
							  } (${getCurrency(currency as any)}${downgradeDetails.newPrice}).

${
	downgradeDetails.lostFeatures.length > 0
		? `You will lose access to the following features at the end of your current billing period:\n\n• ${downgradeDetails.lostFeatures.join(
				'\n• '
		  )}`
		: 'Your plan will change at the end of your current billing period.'
}

The downgrade will take effect at the end of your current billing cycle. You can continue using all current features until then.`
							: ''
					}
					confirmText="Schedule Downgrade"
					cancelText="Cancel"
					onConfirm={handleDowngradeConfirm}
					onCancel={() => setDowngradeConfirmOpen(false)}
					isLoading={processing}
					variant="warning"
				/>

				<ConfirmationModal
					isOpen={cancelDowngradeConfirmOpen}
					title="Cancel Downgrade?"
					message={`Are you sure you want to cancel the scheduled downgrade? 

You will stay on your current ${currentSubscription?.display_name} plan and be charged ${getCurrency(currency as any)}${
						currentSubscription ? (currentSubscription.price / 100).toFixed(0) : '0'
					} on your next billing cycle.`}
					confirmText="Cancel Downgrade"
					cancelText="Keep Downgrade"
					onConfirm={handleCancelDowngrade}
					onCancel={() => setCancelDowngradeConfirmOpen(false)}
					isLoading={processing}
					variant="info"
				/>

				<ConfirmationModal
					isOpen={reactivateConfirmOpen}
					title="Reactivate Subscription?"
					message={`Are you sure you want to reactivate your subscription? 

This will re-enable automatic renewal and you will be charged ${getCurrency(currency as any)}${
						currentSubscription ? (currentSubscription.price / 100).toFixed(0) : '0'
					} for your ${currentSubscription?.display_name} plan on ${
						currentSubscription?.current_period_end || 'the next billing date'
					}.

You can cancel again anytime before the next billing cycle.`}
					confirmText="Reactivate Subscription"
					cancelText="Cancel"
					onConfirm={handleReactivateSubscription}
					onCancel={() => setReactivateConfirmOpen(false)}
					isLoading={processing}
					variant="info"
				/>
			</div>
		</Wrapper>
	);
};

export default Subscription;
